import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currency';
import DevOnly from '../../components/DevOnly';

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch invoice details
  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const response = await apiClient.get(`/financial/invoices/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  const formatCurrency = (amount) => formatCurrencyUtil(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'sent':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load invoice details. Please try again later.
        </Alert>
        <Button onClick={() => navigate('/financial/invoices')} startIcon={<BackIcon />} sx={{ mt: 2 }}>
          Back to Invoices
        </Button>
      </Box>
    );
  }

  // For now, show basic invoice data since we don't have the detail endpoint
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/financial/invoices')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              Invoice #{invoice?.invoice_number || id?.slice(-6)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created {formatDate(invoice?.created_at)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DevOnly>
            <Button startIcon={<EditIcon />} variant="outlined">
              Edit
            </Button>
          </DevOnly>
          <DevOnly>
            <Button startIcon={<EmailIcon />} variant="outlined">
              Send
            </Button>
          </DevOnly>
          <Button startIcon={<PrintIcon />} variant="outlined">
            Print
          </Button>
          <Button startIcon={<DownloadIcon />} variant="outlined">
            Download
          </Button>
          {invoice?.status !== 'paid' && (
            <Button startIcon={<PaymentIcon />} variant="contained" color="primary">
              Record Payment
            </Button>
          )}
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Invoice Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Invoice Number
                </Typography>
                <Typography variant="body1">
                  {invoice?.invoice_number || `INV-${id?.slice(-6)}`}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={invoice?.status || 'Pending'}
                  color={getStatusColor(invoice?.status)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Invoice Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(invoice?.invoice_date || invoice?.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Due Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(invoice?.due_date)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Client
                </Typography>
                <Typography variant="body1">
                  {invoice?.client_name || 'Unknown Client'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(invoice?.total_amount)}
                </Typography>
              </Grid>
            </Grid>

            {/* Note: Line items would go here if we had them */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Invoice line items and details would be displayed here once the backend provides this data.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Payment Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Total Amount:</Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatCurrency(invoice?.total_amount)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Paid:</Typography>
              <Typography variant="body1">
                {formatCurrency(invoice?.paid_amount || 0)}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight="medium">Balance Due:</Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency((invoice?.total_amount || 0) - (invoice?.paid_amount || 0))}
              </Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Activity Log
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Activity history would be displayed here.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvoiceDetail;